import React, { useEffect, useRef } from 'react';
import { FiCheck } from 'react-icons/fi';

/**
 * Live-captions / chat dock. Renders the conversation as bubbles (Wiza vs candidate), a live ghost
 * activity indicator for in-progress speech, and an always-available "type instead of speaking" composer.
 * Used as the right column on desktop and as a slide-over overlay on mobile (pass `onClose`).
 */
const Avatar = ({ mine }) => (
  <div className={`grid h-7 w-7 shrink-0 place-items-center rounded-full text-[11px] font-semibold text-white
    ${mine ? 'bg-emerald-600' : 'bg-gradient-to-br from-indigo-500 to-violet-600'}`}>
    {mine ? 'Y' : 'W'}
  </div>
);

// Clock time under each bubble (e.g. "11:18 AM"); tolerant of a missing/invalid timestamp.
const fmtClock = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const Bubble = ({ role, text, ts, deliveryStatus }) => {
  const mine = role === 'candidate';
  const time = fmtClock(ts);
  return (
    <div className={`flex items-end gap-2 ${mine ? 'flex-row-reverse' : 'flex-row'}`}>
      <Avatar mine={mine} />
      <div className={`flex max-w-[82%] flex-col ${mine ? 'items-end' : 'items-start'}`}>
        <div className={`rounded-2xl px-3.5 py-2 text-sm leading-relaxed shadow-sm
          ${mine ? 'rounded-br-sm bg-violet-600 text-white' : 'rounded-bl-sm bg-slate-100 text-slate-800 ring-1 ring-black/5 dark:bg-white/5 dark:text-slate-100 dark:ring-white/5'}`}>
          <span className={deliveryStatus === 'sending' ? 'animate-pulse' : ''}>{text}</span>
        </div>
        {(time || deliveryStatus) && (
          <span className="mt-0.5 px-1 text-[10px] text-slate-400 dark:text-slate-500">
            {time}{deliveryStatus === 'sending' ? ' · Analyzing.' : deliveryStatus === 'submitting' ? ' · Sending.' : deliveryStatus === 'not_sent' ? ' · Not sent' : deliveryStatus === 'unconfirmed' ? ' · Delivery unconfirmed' : ''}
          </span>
        )}
      </div>
    </div>
  );
};

const CaptionsDock = ({
  messages = [], inputActive = false,
  typed = '', setTyped, onSendTyped, sendDisabled = false,
  onDoneSpeaking, canFinishSpeaking = false,
  onClose,
}) => {
  const endRef = useRef(null);
  const taRef = useRef(null);
  useEffect(() => { endRef.current && endRef.current.scrollIntoView({ behavior: 'smooth' }); }, [messages, inputActive]);
  // Auto-grow the composer textarea up to a few lines as the candidate types a multi-line answer.
  useEffect(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [typed]);

  const submit = () => { if (typed.trim() && !sendDisabled) onSendTyped(typed.trim()); };

  return (
    <div className="flex h-full flex-col rounded-2xl bg-white ring-1 ring-black/5 dark:bg-slate-900/70 dark:ring-white/10">
      {/* header */}
      <div className="flex shrink-0 items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-white/5">
        <span className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-white">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#a78bfa" strokeWidth="2" strokeLinejoin="round">
            <rect x="3" y="5" width="18" height="14" rx="2" /><path d="M8 12h.01M15 12h.01" />
          </svg>
          Live captions
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-normal text-slate-500 dark:bg-white/5 dark:text-slate-400">{messages.length} turns</span>
        </span>
        {onClose && (
          <button type="button" onClick={onClose} aria-label="Close captions" className="grid h-7 w-7 place-items-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-white/10 dark:hover:text-white">
            ✕
          </button>
        )}
      </div>

      {/* Browser recognition stays provisional until the captured audio has been checked. */}
      <div className="shrink-0 border-b border-slate-200 bg-amber-50/70 px-4 py-2 text-[11px] leading-snug text-amber-700 dark:border-white/5 dark:bg-amber-500/10 dark:text-amber-300/90">
        Your words appear after you finish speaking. Audio is checked before your answer is sent.
      </div>

      {/* messages */}
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3">
        {messages.length === 0 && !inputActive && (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-center text-slate-500">
            <span className="text-2xl">💬</span>
            <p className="text-sm">Your conversation will appear here as you speak.</p>
          </div>
        )}
        {messages.map((m, i) => <Bubble key={i} role={m.role} text={m.text} ts={m.ts} deliveryStatus={m.deliveryStatus} />)}
        {inputActive && (
          <div className="flex flex-row-reverse items-end gap-2">
            <Avatar mine />
            <div role="status" aria-label="Listening to your answer" className="max-w-[82%] rounded-2xl rounded-br-sm bg-violet-500/40 px-3.5 py-2 text-sm text-white">
              <span className="inline-block min-w-6 text-center tracking-widest">...</span>
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* composer */}
      <div className="shrink-0 border-t border-slate-200 p-2.5 dark:border-white/5">
        <div className="flex items-end gap-2 rounded-xl bg-slate-100 px-3 py-2 ring-1 ring-black/5 focus-within:ring-primary-500/40 dark:bg-white/5 dark:ring-white/10 dark:focus-within:ring-violet-500/50">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mb-1.5 shrink-0">
            <path d="M21 11.5a8.4 8.4 0 0 1-9 8.3L3 21l1.2-4.5A8.4 8.4 0 1 1 21 11.5z" />
          </svg>
          <textarea
            ref={taRef}
            rows={1}
            value={typed}
            disabled={sendDisabled}
            onChange={(e) => setTyped(e.target.value)}
            // Enter sends; Shift+Enter inserts a newline (so multi-line answers / SQL queries work).
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }}
            placeholder="Type instead of speaking…  (Shift+Enter for a new line)"
            className="max-h-24 min-w-0 flex-1 resize-none bg-transparent text-sm leading-relaxed text-slate-900 placeholder:text-slate-400 focus:outline-none disabled:opacity-50 dark:text-slate-100 dark:placeholder:text-slate-500"
          />
          <button
            type="button"
            onClick={submit}
            disabled={sendDisabled || !typed.trim()}
            aria-label="Send"
            className="grid h-7 w-7 place-items-center rounded-lg bg-violet-600 text-white transition hover:bg-violet-500 disabled:opacity-40"
          >
            <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor"><path d="M3 11l18-8-8 18-2-7-8-3z" /></svg>
          </button>
        </div>
        {onDoneSpeaking && (
          <button
            type="button"
            onClick={onDoneSpeaking}
            disabled={!canFinishSpeaking}
            title="Finish this answer even if background noise keeps the microphone listening"
            className="ml-auto mt-2 flex min-h-9 w-fit items-center justify-center gap-1.5 rounded border border-slate-300 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 disabled:cursor-not-allowed disabled:opacity-40 dark:border-white/20 dark:text-slate-200 dark:hover:bg-white/5"
          >
            <FiCheck className="shrink-0" aria-hidden="true" /> Done speaking
          </button>
        )}
      </div>
    </div>
  );
};

export default CaptionsDock;
